/**
 * RationaleCaptureModal
 *
 * Tool-agnostic rationale capture shown at the moment a participant submits a
 * response. Because it hooks the single shared submit path (LiveToolRunner), it
 * works for every tool without per-tool wiring.
 *
 * Canon 23 §6.4 — rationale must NEVER hard-block a submission. So:
 *   - 'encouraged': optional field, "Skip" is always available.
 *   - 'required':   strong nudge; if left empty the participant gets ONE soft
 *                   confirm ("Submit without explaining?") and can still proceed.
 *
 * The session's rationale_policy is the cycle owner's opt-in; this component
 * honours it without ever trapping the participant.
 */

import { useState } from 'react';
import { MessageSquare, X } from 'lucide-react';
import type { RationalePolicy } from '../../../types/prioritization/toolSession';

interface RationaleCaptureModalProps {
  policy: Exclude<RationalePolicy, 'off'>;
  /** Submit with the (possibly empty) rationale. */
  onSubmit: (rationale: string) => void;
  /** Cancel and return to the tool without submitting. */
  onCancel: () => void;
  isSubmitting?: boolean;
}

export function RationaleCaptureModal({
  policy,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: RationaleCaptureModalProps) {
  const [text, setText] = useState('');
  const [confirmingEmpty, setConfirmingEmpty] = useState(false);
  const required = policy === 'required';
  const isEmpty = text.trim().length === 0;

  const handleSubmit = () => {
    // Required + empty → one soft confirm, never a hard block (Canon 23 §6.4).
    if (required && isEmpty && !confirmingEmpty) {
      setConfirmingEmpty(true);
      return;
    }
    onSubmit(text.trim());
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md bg-[var(--bg-raised)] shadow-xl overflow-hidden">
        <div className="flex items-start justify-between px-5 py-4 border-b border-[var(--rule)]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[var(--slate-dim)] flex items-center justify-center flex-shrink-0">
              <MessageSquare className="h-4 w-4 text-[var(--text-ter)]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[var(--text-pri)]">
                {required ? 'Explain your reasoning' : 'Add your reasoning'}
                {!required && <span className="text-[var(--text-ter)] font-normal"> (optional)</span>}
              </h3>
              <p className="text-xs text-[var(--text-ter)]">
                {required
                  ? 'This session asks everyone to capture the "why" behind their scores.'
                  : 'A short "why" makes your input far more useful to the group.'}
              </p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="text-[var(--text-ter)] hover:text-[var(--text-sec)]"
            aria-label="Back to tool"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-4">
          <textarea
            autoFocus
            value={text}
            onChange={e => {
              setText(e.target.value);
              if (confirmingEmpty) setConfirmingEmpty(false);
            }}
            rows={4}
            placeholder="What drove your judgment? Note any assumptions, evidence, or tradeoffs…"
            className="w-full text-sm border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-indigo-400 focus:border-[var(--slate)] resize-none"
          />
          {confirmingEmpty && (
            <p className="mt-2 text-xs text-[var(--mustard)]">
              You haven&apos;t written anything. You can submit without a note — your input still
              counts — but a sentence here helps the group understand it.
            </p>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-[var(--rule)] bg-[var(--surface)]">
          <button
            onClick={onCancel}
            className="text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)]"
          >
            Back to tool
          </button>
          <div className="flex items-center gap-2">
            {!required && (
              <button
                onClick={() => onSubmit('')}
                disabled={isSubmitting}
                className="text-sm font-medium px-4 py-2 border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] disabled:opacity-50"
              >
                Skip
              </button>
            )}
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="text-sm font-semibold px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50"
            >
              {isSubmitting
                ? 'Submitting…'
                : confirmingEmpty
                  ? 'Submit anyway'
                  : 'Submit response'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RationaleCaptureModal;
